import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Pencil, Truck, X } from 'lucide-react'
import { api } from '../lib/api'
import { formatDateTime } from '../lib/utils'
import { useAuth } from '../context/AuthContext'

// The "who does DrinX buy from" register. First screen in the ERP
// Phase 12 rollout. Purchase Orders reference `suppliers.id`; the
// picker on the PO create form pulls from this list.

interface Supplier {
  id: string
  name: string
  contact_name: string | null
  phone: string | null
  email: string | null
  address: string | null
  notes: string | null
  active: number
  created_at: string
}

export default function Suppliers() {
  const { can } = useAuth()
  const canManage = can(['super_admin', 'operations'])
  const qc = useQueryClient()
  const [modal, setModal] = useState<Supplier | 'new' | null>(null)

  const { data, isLoading } = useQuery<{ suppliers: Supplier[] }>({
    queryKey: ['suppliers'],
    queryFn: () => api.get('/suppliers').then(r => r.data),
  })

  const refresh = () => qc.invalidateQueries({ queryKey: ['suppliers'] })

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Truck size={20} className="text-[#c41e3a]" />
            Suppliers
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            The DrinX supplier register. Purchase orders reference these rows; deactivating hides a supplier from new-PO pickers without breaking historical references.
          </p>
        </div>
        {canManage && (
          <button
            onClick={() => setModal('new')}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#c41e3a] text-white text-sm font-semibold hover:bg-[#a01830]"
          >
            <Plus size={16} />
            New supplier
          </button>
        )}
      </div>

      <div className="bg-[#1a1a1a] rounded-xl border border-white/10 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-sm text-gray-500">Loading…</div>
        ) : (data?.suppliers ?? []).length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500">
            No suppliers yet. Add the first one to unblock purchase orders.
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-white/5 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="text-left px-5 py-3 font-medium">Name</th>
                <th className="text-left px-5 py-3 font-medium">Contact</th>
                <th className="text-left px-5 py-3 font-medium">Phone</th>
                <th className="text-left px-5 py-3 font-medium">Email</th>
                <th className="text-left px-5 py-3 font-medium">Status</th>
                <th className="text-left px-5 py-3 font-medium">Added</th>
                <th className="text-right px-5 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(data?.suppliers ?? []).map(s => (
                <tr key={s.id} className="border-t border-white/10">
                  <td className="px-5 py-3 text-sm text-white font-medium">{s.name}</td>
                  <td className="px-5 py-3 text-sm text-gray-400">{s.contact_name ?? <span className="text-gray-600">—</span>}</td>
                  <td className="px-5 py-3 text-sm text-gray-400 font-mono whitespace-nowrap">{s.phone ?? <span className="text-gray-600">—</span>}</td>
                  <td className="px-5 py-3 text-sm text-gray-400">{s.email ?? <span className="text-gray-600">—</span>}</td>
                  <td className="px-5 py-3">
                    {s.active === 1 ? (
                      <span className="text-xs bg-green-500/15 text-green-400 px-2 py-0.5 rounded-full">Active</span>
                    ) : (
                      <span className="text-xs bg-red-500/15 text-red-400 px-2 py-0.5 rounded-full">Inactive</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-sm text-gray-500">{formatDateTime(s.created_at)}</td>
                  <td className="px-5 py-3 text-right">
                    {canManage && (
                      <button
                        onClick={() => setModal(s)}
                        className="p-1.5 text-gray-500 hover:text-white"
                        title="Edit"
                      >
                        <Pencil size={15} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modal !== null && (
        <SupplierModal
          supplier={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={() => { refresh(); setModal(null) }}
        />
      )}
    </div>
  )
}

function SupplierModal({
  supplier,
  onClose,
  onSaved,
}: {
  supplier: Supplier | null
  onClose: () => void
  onSaved: () => void
}) {
  const isNew = supplier === null
  const [name, setName] = useState(supplier?.name ?? '')
  const [active, setActive] = useState((supplier?.active ?? 1) === 1)
  const [contactName, setContactName] = useState(supplier?.contact_name ?? '')
  const [phone, setPhone] = useState(supplier?.phone ?? '')
  const [email, setEmail] = useState(supplier?.email ?? '')
  const [address, setAddress] = useState(supplier?.address ?? '')
  const [notes, setNotes] = useState(supplier?.notes ?? '')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (!name.trim()) { setError('Name is required'); return }
    setSubmitting(true)
    try {
      const body = {
        name: name.trim(),
        active,
        contact_name: contactName.trim(),
        phone: phone.trim(),
        email: email.trim(),
        address: address.trim(),
        notes: notes.trim(),
      }
      if (isNew) await api.post('/suppliers', body)
      else await api.patch(`/suppliers/${supplier!.id}`, body)
      onSaved()
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } } }
      setError(e.response?.data?.message ?? "Couldn't save.")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-lg bg-[#1a1a1a] border border-white/10 rounded-xl p-6 space-y-4 max-h-[92vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between">
          <h3 className="text-white text-base font-semibold">
            {isNew ? 'Add supplier' : `Edit ${supplier?.name}`}
          </h3>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-white">
            <X size={18} />
          </button>
        </div>

        <Field label="Supplier name">
          <input type="text" value={name} onChange={e => setName(e.target.value)}
            className={inputCls} placeholder="e.g. Al Awda Trading" autoFocus />
        </Field>

        <Field label="Primary contact" hint="Name of the salesperson / account rep DrinX deals with.">
          <input type="text" value={contactName} onChange={e => setContactName(e.target.value)}
            className={inputCls} placeholder="e.g. Ahmed Nasser" />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Phone">
            <input type="text" value={phone} onChange={e => setPhone(e.target.value)}
              className={`${inputCls} font-mono`} placeholder="+962 79 xxx xxxx" />
          </Field>
          <Field label="Email">
            <input type="email" value={email} onChange={e => setEmail(e.target.value)}
              className={inputCls} placeholder="orders@supplier.com" />
          </Field>
        </div>

        <Field label="Address">
          <textarea value={address} onChange={e => setAddress(e.target.value)}
            className={`${inputCls} min-h-[60px]`} placeholder="Warehouse / office address" />
        </Field>

        <Field label="Notes" hint="Payment terms, seasonal availability, preferred delivery day, etc.">
          <textarea value={notes} onChange={e => setNotes(e.target.value)}
            className={`${inputCls} min-h-[80px]`} placeholder="Free-text ops notes" />
        </Field>

        {!isNew && (
          <div className="flex items-center gap-2 pt-2 border-t border-white/10">
            <input
              type="checkbox"
              id="supplier_active"
              checked={active}
              onChange={e => setActive(e.target.checked)}
              className="accent-[#c41e3a]"
            />
            <label htmlFor="supplier_active" className="text-xs text-gray-400">
              Active (uncheck to hide from new-PO pickers)
            </label>
          </div>
        )}

        {error && <p className="text-xs text-red-400">{error}</p>}

        <div className="flex items-center gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 text-sm py-2 rounded-lg border border-[#3a3a3a] text-gray-400 hover:bg-[#242424]"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="flex-1 text-sm py-2 rounded-lg bg-[#c41e3a] text-white font-medium hover:bg-[#a01830] disabled:opacity-50"
          >
            {submitting ? 'Saving…' : isNew ? 'Add supplier' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  )
}

const inputCls =
  'w-full bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#c41e3a]'

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-xs text-gray-500">{label}</label>
      <div className="mt-1">{children}</div>
      {hint && <p className="text-[11px] text-gray-600 mt-1">{hint}</p>}
    </div>
  )
}
